import React, { useEffect, useState } from "react";
import { Spinner } from "reactstrap";
import Button from "../../shared/Button";
import { useAssessmentScale, useGPA } from "../../../hooks/useAssessmentScale";
import swal from "sweetalert";
import { useARRCode } from "../../../context/ARRLevelContext";
import Select from "../../shared/Select";
import { Tooltip } from "@mui/material";

const GradeAssessmentScale = ({
  gradeSelectOptions,
  scaleType,
  type,
  data,
  handleUpdateAssessment,
  updating,
  closeModal,
}) => {
  const { arrLevel } = useARRCode();
  const { loading, createAssessmentScale } = useAssessmentScale();
  const { gpa, loading: gpaLoading, refetchGPA } = useGPA();
  const [gradeAssessmentScale, setGradeAssessmentScale] = useState({
    scaleName: "",
    scoreName: "",
    gpa: [],
  });
  const [recordedIds, setRecordedIds] = useState([]);
  const [deletedRowIds, setDeletedRowIds] = useState([]);

  const isView = type === "view";

  const handleGradeAssessmentChange = (e, index) => {
    if (isView) return;

    const { name, value } = e.target;

    if (index !== undefined) {
      const newGradeAssessment = gradeAssessmentScale?.gpa?.map((item, i) =>
        index === i ? { ...item, [name]: value } : item,
      );

      setGradeAssessmentScale((prev) => ({ ...prev, gpa: newGradeAssessment }));
      return;
    }

    setGradeAssessmentScale((prev) => ({ ...prev, [name]: value }));
  };

  const handleRemoveGpa = (index, id) => {
    const newGpa = gradeAssessmentScale?.gpa
      ?.map((item, i) => (index === i ? null : item))
      .filter(Boolean);

    setGradeAssessmentScale((prev) => ({ ...prev, gpa: newGpa }));

    if (id) {
      setDeletedRowIds((prev) => [...prev, id]);
    }
  };

  const addNewRow = () => {
    const newRow = {
      grade: "",
      percentageMin: "",
      percentageMax: "",
      gradePoint: "",
      gpaMin: "",
      gpaMax: "",
      gpaEvaluation: "",
    };

    setGradeAssessmentScale((prev) => ({
      ...prev,
      gpa: [...gradeAssessmentScale.gpa, newRow],
    }));
  };

  const validateNegativeNumber = (e) => {
    if (e.key === "-" || e.key === "Subtract" || e.key === "e") {
      e.preventDefault();
    }
  };

  const handleSubmit = () => {
    if (gradeAssessmentScale.scaleName.trim() === "") {
      swal("Oops", "Scale Name is required");
      return;
    }
    if (gradeAssessmentScale.scoreName.trim() === "") {
      swal("Oops", "Score Name is required");
      return;
    }

    if (gradeAssessmentScale?.gpa?.length === 0) {
      swal("Oops", "At least one grade is required");
      return;
    }

    const hasEmptyFields = gradeAssessmentScale.gpa?.some(
      (item) =>
        item?.grade === "" ||
        item?.percentageMin === "" ||
        item?.percentageMax === "" ||
        item?.gradePoint === "" ||
        item?.gpaMin === "" ||
        item?.gpaMax === "" ||
        item?.gpaEvaluation?.trim() === "",
    );

    if (hasEmptyFields) {
      swal("Oops", "Please fill all the fields");
      return;
    }

    const formData = {
      assessmentModuleCode: arrLevel,
      assessmentScaleTypeCode: scaleType,
      scaleName: gradeAssessmentScale.scaleName,
      scoreName: gradeAssessmentScale.scoreName,
      assessmentScaleGradePoints: gradeAssessmentScale.gpa,
    };

    if (type === "create") {
      createAssessmentScale(formData);
      setGradeAssessmentScale({ scaleName: "", scoreName: "", gpa: gpa });
    } else {
      const updatedData = { ...formData, id: data.id, delete: deletedRowIds };
      handleUpdateAssessment(updatedData);
    }
  };

  useEffect(() => {
    if (gpa && !data && type === "create") {
      const filteredGPA = gpa.map((item) => ({
        grade: item?.grade,
        percentageMin: item?.percentageMin,
        percentageMax: item?.percentageMax,
        gradePoint: item?.gradePoint,
        gpaMin: item?.gpaMin,
        gpaMax: item?.gpaMax,
        gpaEvaluation: item?.gpaEvaluation,
      }));

      setGradeAssessmentScale((prev) => ({
        ...prev,
        gpa: filteredGPA,
      }));
    }

    if (data && (type === "edit" || isView)) {
      const filteredGPA = data?.assessmentScaleGradePoints?.map((item) => ({
        grade: item?.grade,
        percentageMin: item?.percentageMin,
        percentageMax: item?.percentageMax,
        gradePoint: item?.gradePoint,
        gpaMin: item?.gpaMin,
        gpaMax: item?.gpaMax,
        gpaEvaluation: item?.gpaEvaluation,
        id: item?.id,
      }));

      setGradeAssessmentScale((prev) => ({
        scaleName: data?.scaleName,
        scoreName: data?.scoreName,
        gpa: filteredGPA,
      }));

      setRecordedIds(data?.recordedIds);
    }
  }, [gpa, data, type]);

  return (
    <>
      <div className={`arr-form grid ${type !== "create" ? "noShadow" : ""}`}>
        <div className="form-input">
          <label className="required" htmlFor="scaleName">
            Scale Name
          </label>
          <input
            type="text"
            value={gradeAssessmentScale?.scaleName}
            id="scaleName"
            name="scaleName"
            onChange={handleGradeAssessmentChange}
            className="form-control"
            readOnly={isView}
            disabled={isView}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="scoreName">
            Score Name
          </label>
          <input
            type="text"
            value={gradeAssessmentScale?.scoreName}
            name="scoreName"
            id="scoreName"
            onChange={handleGradeAssessmentChange}
            className="form-control"
            readOnly={isView}
            disabled={isView}
          />
        </div>
      </div>

      {type === "create" && (
        <div style={{ margin: "24px 0" }} className="action-btns end">
          <Button disabled={gpaLoading} type="button" onClick={refetchGPA}>
            {gpaLoading ? (
              <Spinner size={"sm"} color="white" />
            ) : (
              "Restore Default GPAs"
            )}
          </Button>
        </div>
      )}

      <div className="arr-table-container">
        <table aria-label="arr-table" className="arr-table">
          <thead>
            <tr>
              <th>
                <span className="required">Grade</span>
              </th>
              <th>
                <span className="required">Percentage</span>
              </th>
              <th>
                <span className="required">Grade Point</span>
              </th>
              <th>
                <span className="required">Grade Point Average</span>
              </th>
              <th>
                <span className="required">Grade Evaluation</span>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {gpaLoading ? (
              <tr>
                <td colSpan={6}>
                  <div className="arr-spinner">
                    <div className="loader">
                      <Spinner size={"md"} color="primary"></Spinner>
                    </div>
                    <div style={{ fontSize: "13px", marginTop: "15px" }}>
                      Please wait !! Processing.....
                    </div>
                  </div>
                </td>
              </tr>
            ) : !gpaLoading && gradeAssessmentScale?.gpa?.length > 0 ? (
              gradeAssessmentScale?.gpa?.map((item, index) => {
                const isRecorded =
                  item?.id != null &&
                  recordedIds?.length > 0 &&
                  recordedIds.includes(item?.id);
                return (
                  <tr key={index}>
                    <td>
                      <Select
                        name={"grade"}
                        options={gradeSelectOptions}
                        value={
                          gradeSelectOptions?.find(
                            (o) => o.value === item.grade,
                          ) || null
                        }
                        onChange={(option) => {
                          const newGrade = gradeAssessmentScale?.gpa?.map(
                            (item, i) =>
                              index === i
                                ? { ...item, grade: option?.value }
                                : item,
                          );

                          setGradeAssessmentScale((prev) => ({
                            ...prev,
                            gpa: newGrade,
                          }));
                        }}
                        isClearable={false}
                        styles={{
                          control: { minWidth: "200px" },
                        }}
                        isDisabled={isView}
                      />
                    </td>

                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <input
                          aria-label="minPercentage"
                          style={{ width: "80px" }}
                          type="number"
                          min={0}
                          className="form-control"
                          value={item?.percentageMin}
                          name="percentageMin"
                          onKeyDown={validateNegativeNumber}
                          onChange={(e) =>
                            handleGradeAssessmentChange(e, index)
                          }
                          readOnly={isView}
                          disabled={isView}
                        />
                        <span>to</span>
                        <input
                          aria-label="maxPercentage"
                          min={0}
                          style={{ width: "80px" }}
                          type="number"
                          className="form-control"
                          value={item?.percentageMax}
                          name="percentageMax"
                          onKeyDown={validateNegativeNumber}
                          onChange={(e) =>
                            handleGradeAssessmentChange(e, index)
                          }
                          readOnly={isView}
                          disabled={isView}
                        />
                      </div>
                    </td>

                    <td>
                      <input
                        aria-label="gradePoint"
                        style={{ width: "80px" }}
                        type="number"
                        className="form-control"
                        value={item?.gradePoint}
                        name="gradePoint"
                        min={0}
                        onKeyDown={validateNegativeNumber}
                        onChange={(e) => handleGradeAssessmentChange(e, index)}
                        readOnly={isView}
                        disabled={isView}
                      />
                    </td>

                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <input
                          aria-label="minGPA"
                          style={{ width: "80px" }}
                          type="number"
                          className="form-control"
                          value={item?.gpaMin}
                          name="gpaMin"
                          min={0}
                          onKeyDown={validateNegativeNumber}
                          onChange={(e) =>
                            handleGradeAssessmentChange(e, index)
                          }
                          readOnly={isView}
                          disabled={isView}
                        />
                        <span>to</span>
                        <input
                          aria-label="maxGPA"
                          style={{ width: "80px" }}
                          type="number"
                          className="form-control"
                          value={item?.gpaMax}
                          name="gpaMax"
                          min={0}
                          onKeyDown={validateNegativeNumber}
                          onChange={(e) =>
                            handleGradeAssessmentChange(e, index)
                          }
                          readOnly={isView}
                          disabled={isView}
                        />
                      </div>
                    </td>

                    <td>
                      <input
                        aria-label="gpaEvaluation"
                        style={{ width: "200px" }}
                        type="text"
                        className="form-control"
                        value={item?.gpaEvaluation}
                        name="gpaEvaluation"
                        onChange={(e) => handleGradeAssessmentChange(e, index)}
                        readOnly={isView}
                        disabled={isView}
                      />
                    </td>

                    <td>
                      {(type === "create" || type === "edit") && (
                        <Tooltip
                          title={
                            isRecorded
                              ? "You cannot remove it because this grade has already been recorded in the subject/general assessment."
                              : ""
                          }
                        >
                          <div>
                            <Button
                              onClick={() => handleRemoveGpa(index, item?.id)}
                              variant="danger"
                              size="sm"
                              disabled={isRecorded}
                            >
                              Remove
                            </Button>
                          </div>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isView && (
        <>
          <div style={{ marginTop: "17px" }} className="action-btns end">
            <Button size="sm" onClick={addNewRow}>
              Add Row
            </Button>
          </div>

          <div className="action-btns end" style={{ marginTop: "24px" }}>
            {type !== "create" && (
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
            )}

            <Button disabled={loading.create} onClick={handleSubmit}>
              {loading.create || updating ? (
                <Spinner size={"sm"} color="white" />
              ) : type === "edit" ? (
                "Update Assessment Scale"
              ) : (
                "Save Assessment Scale"
              )}
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default GradeAssessmentScale;
